import { useEffect, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { EvangileItem, getTodayDateKey, normalizeEvangile } from './useEvangile';

const FALLBACK_LANG = 'en';

/**
 * Récupère le commentaire du jour (ou le dernier publié) pour `lang`.
 * Si la langue ne dispose d'aucun commentaire, bascule sur l'anglais.
 * Léger : ne charge qu'un seul document (teaser du hero).
 */
export const useLatestEvangile = (lang: string) => {
  const [item, setItem] = useState<EvangileItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [effectiveLang, setEffectiveLang] = useState(lang);

  useEffect(() => {
    let cancelled = false;

    const fetchLatest = async (l: string): Promise<EvangileItem | null> => {
      const todayKey = getTodayDateKey();
      const q = query(
        collection(db, `evangile_${l}`),
        where('date', '<=', todayKey),
        orderBy('date', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(q);
      const doc = snapshot.docs[0];
      return doc ? normalizeEvangile(doc) : null;
    };

    (async () => {
      setLoading(true);
      try {
        let found = await fetchLatest(lang);
        let usedLang = lang;
        if (!found && lang !== FALLBACK_LANG) {
          found = await fetchLatest(FALLBACK_LANG);
          usedLang = FALLBACK_LANG;
        }
        if (!cancelled) {
          setItem(found);
          setEffectiveLang(usedLang);
        }
      } catch (err) {
        console.error('[useLatestEvangile] error:', err);
        if (!cancelled) setItem(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang]);

  return { item, loading, effectiveLang };
};
